// EventBusModule - A type-safe event bus for component communication 🏴‍☠️⭐
// This allows components to communicate without tight coupling, arrr!

import { ChallengeService } from '@duck/api-definitions'
import { useEffect } from 'react'
import _ from 'lodash'

// Generic event interface that all events must extend
export interface BaseEvent {
  type: string
  timestamp: number
  id: string
}

// Event listener function type
export type EventListener<T extends BaseEvent = BaseEvent> = (event: T) => void

// Event listener with metadata for management
interface EventListenerEntry {
  listener: EventListener<any>
  once: boolean
  id: string
}

// Type-safe event map - extend this interface to add new event types
export interface EventMap {
  'chart:added': BaseEvent & { chart: ChallengeService.Chart }
  'charts:triggerRedraw': BaseEvent & { originId?: string }
  'layout:refresh': BaseEvent
  'workspace:repaintSolution': BaseEvent
  'codeInput:insert': BaseEvent & { value: string }
  'codeInput:cmdEnter': BaseEvent & { query?: string }
  'solution:insertHtml': BaseEvent & { value: string; deleteButton?: boolean }
}

// Main EventBus class
export class EventBus {
  private listeners: Map<string, EventListenerEntry[]> = new Map()
  private eventHistory: BaseEvent[] = []
  private maxHistorySize = 100

  // Generate unique ID for events and listeners
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  // Subscribe to an event type
  public on<K extends keyof EventMap>(
    eventType: K,
    listener: EventListener<EventMap[K]>,
    options: { once?: boolean } = {}
  ): () => void {
    const listenerId = this.generateId()
    const eventListeners = this.listeners.get(eventType) || []
    
    const listenerEntry: EventListenerEntry = {
      listener: listener as EventListener<any>,
      once: options.once || false,
      id: listenerId
    }
    
    eventListeners.push(listenerEntry)
    this.listeners.set(eventType, eventListeners)

    // Return unsubscribe function
    return () => this.off(eventType, listenerId)
  }

  // Subscribe to an event type (fires only once)
  public once<K extends keyof EventMap>(
    eventType: K,
    listener: EventListener<EventMap[K]>
  ): () => void {
    return this.on(eventType, listener, { once: true })
  }

  // Unsubscribe from an event type
  public off<K extends keyof EventMap>(eventType: K, listenerId?: string): void {
    const eventListeners = this.listeners.get(eventType)
    if (!eventListeners) return

    if (listenerId) {
      // Remove specific listener by ID
      const updatedListeners = eventListeners.filter(entry => entry.id !== listenerId)
      this.listeners.set(eventType, updatedListeners)
    } else {
      // Remove all listeners for this event type
      this.listeners.delete(eventType)
    }
  }

  // Emit an event to all subscribers
  public emit<K extends keyof EventMap>(
    eventType: K,
    eventData: Omit<EventMap[K], 'type' | 'timestamp' | 'id'>
  ): void {
    // Create the full event object
    const event: EventMap[K] = {
      ...eventData,
      type: eventType,
      timestamp: Date.now(),
      id: this.generateId()
    } as EventMap[K]

    // Add to history
    this.addToHistory(event)

    // Get listeners for this event type
    const eventListeners = this.listeners.get(eventType) || []
    
    // Create a copy to iterate over (in case listeners modify the array)
    const listenersToNotify = [...eventListeners]
    
    // Notify all listeners
    listenersToNotify.forEach(entry => {
      try {
        // console.log('🏴‍☠️ Emitting event:', eventType, event)
        entry.listener(event)
      } catch (error) {
        console.error(`🏴‍☠️ Error in event listener for '${eventType}':`, error)
      }
    })

    // Remove 'once' listeners after they've been called
    const remainingListeners = eventListeners.filter(entry => !entry.once)
    if (remainingListeners.length !== eventListeners.length) {
      this.listeners.set(eventType, remainingListeners)
    }
  }

  // Add event to history for debugging
  private addToHistory(event: BaseEvent): void {
    this.eventHistory.push(event)
    
    // Keep history size manageable
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory = this.eventHistory.slice(-this.maxHistorySize)
    }
  }

  // Get recent event history (useful for debugging)
  public getEventHistory(eventType?: keyof EventMap): BaseEvent[] {
    if (eventType) {
      return this.eventHistory.filter(event => event.type === eventType)
    }
    return [...this.eventHistory]
  }

  // Clear all listeners (useful for cleanup)
  public clear(): void {
    this.listeners.clear()
    this.eventHistory = []
  }

  // Get debug info about current listeners
  public getListenerInfo(): Record<string, number> {
    const info: Record<string, number> = {}
    for (const [eventType, listeners] of this.listeners.entries()) {
      info[eventType] = listeners.length
    }
    return info
  }
}

// Create singleton instance
export const eventBus = new EventBus()

// Convenience hooks for React components
export const useEventBus = () => {
  return {
    on: eventBus.on.bind(eventBus),
    once: eventBus.once.bind(eventBus),
    off: eventBus.off.bind(eventBus),
    emit: eventBus.emit.bind(eventBus),
    getHistory: eventBus.getEventHistory.bind(eventBus),
    getListenerInfo: eventBus.getListenerInfo.bind(eventBus)
  }
}

// React hook for subscribing to events with automatic cleanup
// Import React types at the top of your component file when using this hook
export const useEventListener = <K extends keyof EventMap>(
  eventType: K,
  listener: EventListener<EventMap[K]>,
  deps: any[] = []
): void => {
  useEffect(() => {
    const unsubscribe = eventBus.on(eventType, listener)
    return unsubscribe
  }, deps)
}

// React hook for subscribing to events with debounced handler (delayMS, eventType, handler, deps)
export const useDebouncedEventListener = <K extends keyof EventMap>(
  delayMS: number,
  eventType: K,
  listener: EventListener<EventMap[K]>,
  deps: unknown[] = []
): void => {
  useEffect(() => {
    const debouncedListener = _.debounce(listener, delayMS)
    const unsubscribe = eventBus.on(eventType, debouncedListener as EventListener<EventMap[K]>)
    return () => {
      debouncedListener.cancel()
      unsubscribe()
    }
  }, deps)
}

export const useEventEmitter = <K extends keyof EventMap>(
  eventType: K,
  throttleTime?: number
): (eventData: Omit<EventMap[K], 'type' | 'timestamp' | 'id'>) => void => {
  const { emit } = useEventBus()
  if (throttleTime) {
    return _.throttle((eventData) => {
      emit(eventType, eventData)
    }, throttleTime)
  }
  return (eventData) => {
    emit(eventType, eventData)
  }
}